/**
 * Phase model for an intervention (repair) — the 5-stage spine shared by the
 * detail page, the list mini-bar and the unified {@link TaskCard}.
 *
 * Pure types + constants, NO logic: the derivation lives in
 * `pages/apps/repairs/repair-detail/services/intervention-phase.util.ts`, which
 * re-exports these so existing `./intervention-phase.util` imports keep working.
 * Kept here in `shared/definitions` so the shared `TaskCard` can carry a
 * precomputed `phase` without `shared/` depending on a feature module.
 */

/** Stable phase identity at each spine position (i18n label key suffix). */
export const INTERVENTION_PHASES = [
  'reported', // 0 — raised, no contractor engaged
  'provider', // 1 — a contractor is assigned / a request was sent
  'quote', // 2 — a quote was received and awaits validation (work request only)
  'intervention', // 3 — work underway (visit active / accepted / in progress / waiting parts)
  'closed', // 4 — terminal (completed/resolved, or canceled — see terminalKind)
] as const;

export type InterventionPhaseKey = (typeof INTERVENTION_PHASES)[number];
export type InterventionPhaseIndex = 0 | 1 | 2 | 3 | 4;

/** Number of spine positions / list mini-bar segments. */
export const INTERVENTION_PHASE_COUNT = INTERVENTION_PHASES.length;

/** How a terminal phase ended — `closed` renders green, `canceled` neutral. */
export type InterventionTerminalKind = 'closed' | 'canceled';

export interface InterventionPhase {
  index: InterventionPhaseIndex;
  key: InterventionPhaseKey;
  /** Non-null only at the terminal position (index 4). */
  terminalKind: InterventionTerminalKind | null;
  /** True when the "Devis" phase does not apply (manual assignment). */
  quoteSkipped: boolean;
}
